// 11 · Saisie COL MNC & Documents — étape de RESTITUTION.
// FlashCP ne collecte plus, il dicte ce qu'il faut saisir et ce qu'il faut
// imprimer. Les dix premières étapes collectent, celle-ci rend.
//
// Les montants sont calculés à partir des règlements du jour, en respectant la
// règle du montant réel : un PAD dont le justificatif était anormal part avec
// ce qui est RÉELLEMENT entré, pas avec ce qu'ORIKA annonce. La liste exacte et
// son ordre restent à relever devant COL MNC (point ouvert O8) — d'où le
// bandeau : ces totaux se vérifient avant d'être saisis les yeux fermés.
//
// Ce qui se copie n'est PAS ce qui s'affiche. L'écran montre « 1 234,56 € »
// parce qu'un humain lit mieux ainsi ; le presse-papier reçoit « 1234,56 »,
// parce que c'est un champ montant d'un autre logiciel qui le reçoit et qu'un
// symbole € ou une espace insécable de milliers s'y fait refuser ou tronquer.
//
// Les documents ne sont plus une liste en dur : ils viennent de REPORTS_META,
// c'est-à-dire du moteur PDF client lui-même. Une liste écrite à la main à côté
// du moteur finit toujours par mentir le jour où un rapport est ajouté.

import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { isPadPayment, REMISE_GROUPS } from 'sotcp-core/csvParser'
import { sumEnveloppesJ1 } from 'sotcp-core/inventaireCoffreCalc'
import { useRun } from '@/data/RunProvider'
import * as api from '@/data/api'
import { etatCoffre, etatPad } from '@/domain/derive'
import {
  apportsFlottants, argentTrouve, bonsNonClasses, bonsParGroupe, chequesManuels, coffreMonnaie,
  ecartsDeCaisse, lad, livraisonsMonnaie,
} from '@/domain/colmnc'
import { useReglages } from '@/data/reglages'
import { couleurEcart, fmtEcart, motEcart } from '@/domain/ecart'
import { fmtEur, pluriel } from '@/lib/format'
import { messageErreur } from '@/lib/errors'
import { appDataFromRapport, downloadAll, downloadReport, REPORTS_META } from '@/pdf/pdfReports'
import { htmlFeuilleSaisie } from '@/pdf/saisiePaper'
import { imprimerDocument } from '@/pdf/imprimer'
import { feuilleSaisie } from '@/domain/saisie'
import { Card, Chantier, Chip, Notice, Vide } from '@/ui/primitives'
import { useToast } from '@/ui/Toast'
import type { PropsEtape } from './types'

const CHEQUES = [2, 3]

/**
 * La confirmation de copie se fait à l'ENCRE PLEINE, jamais avec une couleur.
 * Un `btn ok` (vert) posé sur la ligne « Écart de coffre » mettrait un vert à
 * dix pixels d'un écart critique rouge : debout, à 7 h, ça se lit « cette ligne
 * est conforme ». Le noir plein se voit tout autant et ne qualifie rien.
 */
const FLASH_COPIE: CSSProperties = {
  background: 'var(--ink)', borderColor: 'var(--ink)', color: 'var(--surface)',
}

export default function ColMnc({ etape, rapport, lectureSeule }: PropsEtape) {
  const { date, padAttente } = useRun()
  const reglages = useReglages()
  const toast = useToast()

  const [coches, setCoches] = useState<Record<string, boolean>>(
    () => (etape.avancement?.docs ?? {}) as Record<string, boolean>,
  )
  const [enCours, setEnCours] = useState<Record<string, boolean>>({})
  const [toutEnCours, setToutEnCours] = useState(false)
  const [copiee, setCopiee] = useState<string | null>(null)
  // La saisie COL MNC est un aller-retour par ligne : on copie, on quitte
  // l'écran des yeux, on colle, on revient. Un client, un téléphone, et on ne
  // sait plus où on en était — on saute une ligne ou on en colle deux fois.
  // On garde donc la trace de ce qui est déjà parti, avec la VALEUR copiée :
  // si le rapport est réimporté et que le chiffre bouge, la trace tombe d'elle-
  // même et la ligne redevient à copier. Volontairement hors base : c'est une
  // aide au report en cours, pas un état de l'étape.
  const [copiees, setCopiees] = useState<Record<string, string>>({})

  // Un téléchargement peut se terminer plusieurs rendus plus tard : la closure
  // sur `coches` serait périmée. Le ref porte toujours la dernière intention.
  const dernier = useRef(coches)
  // Nombre d'écritures parties et non revenues — voir la synchronisation ci-dessous.
  const enVol = useRef(0)
  const minuteur = useRef<number | undefined>(undefined)
  // Un PDF met plusieurs secondes à se générer : on peut avoir quitté l'étape
  // avant la fin. Rien ne doit être posé dans un composant démonté.
  const vivant = useRef(true)
  useEffect(() => () => {
    vivant.current = false
    window.clearTimeout(minuteur.current)
  }, [])

  // Le temps réel pousse fc_etapes (RunProvider) : un second poste peut cocher
  // pendant qu'on regarde, et la reprise après interruption relit l'avancement.
  // On adopte donc l'état du serveur — sauf pendant nos propres écritures, dont
  // l'écho reviendrait après coup et ferait clignoter les cases.
  useEffect(() => {
    if (enVol.current > 0) return
    const serveur = (etape.avancement?.docs ?? {}) as Record<string, boolean>
    if (memesCoches(serveur, dernier.current)) return
    dernier.current = serveur
    setCoches(serveur)
  }, [etape.avancement])

  const enregistrer = (calcul: (c: Record<string, boolean>) => Record<string, boolean>) => {
    // Choke point unique de la saisie : en lecture seule rien ne s'écrit, ni en
    // base ni à l'écran. L'impression, elle, reste permise — c'est une lecture.
    if (lectureSeule) return
    const next = calcul(dernier.current)
    dernier.current = next
    setCoches(next)
    enVol.current += 1
    // L'avancement alimente aussi la reprise après interruption (§9.1).
    void api.toucherEtape(date, etape.code, { docs: next })
      // Une coche qui n'atteint pas le serveur disparaît à la reprise : se taire
      // (l'ancien `.catch(() => {})`) laissait croire qu'elle était enregistrée.
      .catch((e: unknown) => toast(`COCHE NON ENREGISTRÉE — ${messageErreur(e)}`, true))
      .finally(() => { enVol.current = Math.max(0, enVol.current - 1) })
  }

  const { lignes: montants, ecarte } = useMemo(() => calculer(rapport), [rapport])

  // Les quatre écrans de COL MNC. Rien n'est calculé ici : tout vient de
  // @/domain/colmnc, qui lit ce que les étapes précédentes ont produit.
  const sections = useMemo(() => {
    const coffre = coffreMonnaie(rapport)
    const livraisons = livraisonsMonnaie(rapport)
    const trouve = argentTrouve(rapport)
    const ecarts = ecartsDeCaisse(rapport)
    const attente = lad(padAttente)
    const cm = chequesManuels(rapport)
    const bons = bonsParGroupe(date, rapport)
    const nonClasses = bonsNonClasses(date, rapport)

    return [
      {
        id: 'param', numero: '1', titre: 'Paramétrage',
        sous: 'Apports en fonds de caisse flottants — le total de la personne ou de la machine, rien de plus.',
        vide: 'Aucun apport aujourd’hui — ni caissier (étape 02), ni monnayeur (étape 02A).',
        blocs: [{ titre: 'Apports', lignes: apportsFlottants(rapport, reglages.caissiers) }],
      },
      {
        id: 'autres', numero: '2', titre: 'Autres opérations',
        sous: 'Livraisons de monnaie et argent trouvé — saisis à l’étape 10, restitués ici.',
        vide: 'Aucune livraison de monnaie ni argent trouvé. Ils se saisissent aux entrées de coffre, étape 10.',
        blocs: [
          {
            titre: 'Livraison de monnaie',
            lignes: livraisons.nb > 0
              ? [{ cle: 'Total des livraisons', source: `${pluriel(livraisons.nb, 'entrée')} — étape 10`, valeur: livraisons.total }]
              : [],
          },
          { titre: 'Argent trouvé', lignes: trouve },
        ],
      },
      {
        id: 'ecarts', numero: '3', titre: 'Écarts de caisse',
        sous: 'Une ligne par poste, pas par personne : un même caissier peut en porter plusieurs, de natures différentes.',
        vide: 'Aucun écart de caisse ni règlement non conforme aujourd’hui.',
        chip: ecarts.some((l) => !l.texte) ? 'COMMENTAIRE MANQUANT' : undefined,
        chipTon: 'warn' as const,
        lampe: ecarts.length === 0 ? undefined : ecarts.some((l) => !l.texte) ? 'warn' as const : 'ok' as const,
        blocs: [{ titre: 'Lignes', lignes: ecarts }],
      },
      {
        id: 'coffre', numero: '4', titre: 'Section coffre',
        sous: 'Coffre monnaie puis annexe. Les billets sont consolidés : comptés en vrac plus les enveloppes scellées du vidage SCO.',
        vide: 'L’inventaire de l’étape 10 n’a pas été commencé — rien à dicter.',
        chip: nonClasses > 0 ? `${nonClasses} BON NON CLASSÉ` : undefined,
        chipTon: 'stop' as const,
        blocs: [
          { titre: 'Coffre monnaie — pièces et rouleaux', lignes: coffre.pieces },
          { titre: 'Coffre monnaie — billets (consolidés)', lignes: coffre.billets },
          { titre: 'Fonds de caisses fixes', lignes: coffre.fixes },
          { titre: 'Sorties de coffre', lignes: coffre.sorties },
          { titre: 'Entrées de coffre', lignes: coffre.entrees },
          {
            titre: 'Annexe — LAD (PAD en attente, toutes journées)',
            lignes: attente.nb > 0
              ? [...attente.lignes,
                 { cle: 'Total LAD', source: `${pluriel(attente.nb, 'dossier')} ouvert(s)`, valeur: attente.total }]
              : [],
          },
          {
            titre: 'Annexe — chèques manuels',
            lignes: cm.nb > 0
              ? [{ cle: `${cm.nb} chèque${cm.nb > 1 ? 's' : ''} manuel${cm.nb > 1 ? 's' : ''}`, source: 'règlement CHQ MANUEL', valeur: cm.total }]
              : [],
          },
          { titre: 'Annexe — bons (TR et CAP exclus)', lignes: bons },
        ],
      },
    ]
  }, [date, rapport, padAttente, reglages.caissiers])
  const appData = useMemo(
    () => appDataFromRapport(date, (rapport ?? {}) as Record<string, unknown>),
    [date, rapport],
  )

  // La feuille de papier qu'on emporte devant COL MNC. Elle ne remplace pas la
  // dictée à l'écran — elle la suit ailleurs, sur le poste où la saisie se fait
  // vraiment, avec une case à cocher par bloc.
  const imprimerFeuille = () => {
    const html = htmlFeuilleSaisie({
      f: feuilleSaisie(date, rapport ?? null, reglages.caissiers, padAttente),
    })
    if (imprimerDocument(html, `Saisie COL MNC — ${date}`) === 'bloque') {
      toast('FENÊTRE BLOQUÉE — AUTORISE LES FENÊTRES SURGISSANTES POUR IMPRIMER', true)
    }
  }

  // Les dossiers encore ouverts venus des journées précédentes : leur argent
  // n'est pas entré aujourd'hui non plus, et il ne figure dans aucun total.
  const attenteAnciennes = padAttente.filter(
    (p) => p.statut === 'en_attente' && p.journee_origine !== date,
  )

  // Un rapport sans données n'a rien à imprimer aujourd'hui : il ne compte pas
  // dans le décompte, sinon l'étape ne serait jamais complète.
  const aImprimer = useMemo(() => REPORTS_META.filter((r) => r.hasData(appData)), [appData])
  const nbCoches = aImprimer.filter((r) => coches[r.id]).length
  // Zéro rapport à imprimer est un état COMPLET, pas un état en attente : sans
  // cela une journée sans aucun document laisserait la lampe au rouge à vie.
  const tousCoches = nbCoches === aImprimer.length

  /** Le commentaire d'une ligne — recopié tel quel, sans mise en forme. */
  const copierLibre = async (l: Ligne) => {
    if (!l.texte) return
    if (!(await copierTexte(l.texte))) {
      toast('COPIE REFUSÉE PAR LE NAVIGATEUR — SÉLECTIONNE LE TEXTE À LA SOURIS', true)
      return
    }
    toast(`COPIÉ — ${l.cle}`)
  }

  const copier = async (l: Ligne) => {
    const texte = l.nombre ? String(l.valeur) : pourCopie(l.valeur)
    const ok = await copierTexte(texte)
    if (!vivant.current) return
    if (!ok) {
      toast('COPIE REFUSÉE PAR LE NAVIGATEUR — SÉLECTIONNE LE MONTANT À LA SOURIS', true)
      return
    }
    toast(`COPIÉ — ${l.cle} · ${texte}`)
    window.clearTimeout(minuteur.current)
    setCopiees((c) => ({ ...c, [cleLigne(l)]: texte }))
    setCopiee(cleLigne(l))
    minuteur.current = window.setTimeout(() => { if (vivant.current) setCopiee(null) }, 2200)
  }

  const imprimer = async (id: string) => {
    setEnCours((e) => ({ ...e, [id]: true }))
    try {
      await downloadReport(id, appData)
      // §7.10 : la case se coche au fur et à mesure de l'impression.
      enregistrer((c) => ({ ...c, [id]: true }))
    } catch (e) {
      toast(messageErreur(e), true)
    } finally {
      if (vivant.current) setEnCours((e) => { const n = { ...e }; delete n[id]; return n })
    }
  }

  const toutImprimer = async () => {
    setToutEnCours(true)
    try {
      await downloadAll(appData)
      // downloadAll ne fusionne que les rapports pourvus : cocher les autres
      // ferait croire à une impression qui n'a pas eu lieu.
      enregistrer((c) => {
        const n = { ...c }
        for (const r of aImprimer) n[r.id] = true
        return n
      })
    } catch (e) {
      toast(messageErreur(e), true)
    } finally {
      if (vivant.current) setToutEnCours(false)
    }
  }

  const coffreInconnu = montants.some((m) => m.indetermine)
  // Ce qui rend la dictée sûre : un coffre compté et des PAD tous statués. Le
  // dire EN TÊTE plutôt que sur une carte au milieu — c'est la première chose à
  // savoir avant de recopier quoi que ce soit dans COL MNC.
  const chiffresFiables = !coffreInconnu && ecarte.nonStatues === 0

  // Où j'en suis dans le report. Le compte reste NEUTRE même à 8/8 : il dit ce
  // qui est passé par le presse-papier, pas que COL MNC est juste.
  const copiables = montants.filter((m) => !m.indetermine)
  const dejaCopie = (m: Ligne) =>
    !m.indetermine && copiees[cleLigne(m)] === (m.nombre ? String(m.valeur) : pourCopie(m.valeur))
  const nbCopiees = copiables.filter(dejaCopie).length

  return (
    <>
      <Notice c={chiffresFiables ? 'act' : 'warn'}>
        <b>Étape de restitution</b> — FlashCP ne collecte plus : il dicte, dans l’ordre où tu
        remontes tes écrans COL MNC. Un clic sur un montant le met dans le presse-papier,{' '}
        <span className="mo">sans le symbole €</span>, prêt à coller.
        {!chiffresFiables && (
          <> <b>Attention :</b> {coffreInconnu ? 'le coffre n’est pas compté' : ''}
          {coffreInconnu && ecarte.nonStatues > 0 ? ' et ' : ''}
          {ecarte.nonStatues > 0 ? `${ecarte.nonStatues} PAD n’ont pas d’issue choisie` : ''} —
          tous les chiffres ci-dessous ne sont pas encore sûrs.</>
        )}
      </Notice>

      {/* Le papier vient AVANT la dictée : c'est le geste qui ouvre la saisie.
          Descendre chercher le bouton après quatre écrans de chiffres, c'est
          l'imprimer une fois sur deux — et saisir sans, l'autre fois. */}
      <div style={{ margin: '10px 0' }}>
        <button type="button" className="btn pri lg" style={{ width: '100%' }}
                onClick={imprimerFeuille}>
          Imprimer la feuille de saisie — 1 page A4
        </button>
      </div>

      <div className="two">
        <div>
          {/* Les quatre écrans de COL MNC, dans l'ordre où Kamel les REMONTE :
              du paramétrage vers la section coffre, c'est-à-dire l'inverse de
              l'ordre du logiciel. Le dicter dans l'ordre du logiciel obligerait
              à sauter d'un écran à l'autre à chaque ligne. */}
          {sections.map((sec) => {
            const vide = sec.blocs.every((b) => b.lignes.length === 0)
            return (
              <section className="dictee" key={sec.id}>
                <div className="dictee-hd">
                  <h2>
                    <span className="n">{sec.numero}</span>
                    {sec.titre}
                    {sec.chip && <Chip c={sec.chipTon}>{sec.chip}</Chip>}
                  </h2>
                  <p>{sec.sous}</p>
                </div>
                {vide
                  ? <Vide>{sec.vide}</Vide>
                  : sec.blocs.filter((b) => b.lignes.length > 0).map((bloc) => (
                    <div key={bloc.titre}>
                      <div className="dictee-bloc">{bloc.titre}</div>
                      {bloc.lignes.map((m, i) => {
                        const p = ligneDeLaPaire(m)
                        return (
                          <Rangee key={`${bloc.titre}-${m.cle}-${i}`} m={m}
                                  deja={dejaCopie(m)} fraiche={copiee === cleLigne(m) && dejaCopie(m)}
                                  copier={() => void copier(m)}
                                  copierTexte={() => void copierLibre(m)}
                                  copierPaire={p ? () => void copier(p) : undefined}
                                  dejaPaire={p ? dejaCopie(p) : false}
                                  fraichePaire={!!p && copiee === cleLigne(p) && dejaCopie(p)} />
                        )
                      })}
                    </div>
                  ))}
              </section>
            )
          })}

          <section className="dictee">
            <div className="dictee-hd">
              <h2>
                <span className="n">5</span>
                Remise du jour
                <Chip>{nbCopiees}/{copiables.length} COPIÉS</Chip>
              </h2>
              <p>
                Les totaux de la remise, hors des quatre écrans ci-dessus. Les PAD anormaux y
                entrent avec le montant RÉELLEMENT reçu — sinon les totaux seraient faux.
              </p>
            </div>
            {montants.map((m) => (
              <Rangee key={m.cle} m={m} deja={dejaCopie(m)}
                      fraiche={copiee === cleLigne(m) && dejaCopie(m)} copier={() => void copier(m)} />
            ))}
          </section>

          {coffreInconnu && (
            <div style={{ marginTop: 8 }}>
              <Notice c="warn">
                <b>L’écart de coffre n’est pas connu.</b> L’inventaire de l’étape 10 n’a pas été
                commencé : un coffre non compté ne donne pas un écart nul, il ne donne aucun
                écart. Rien à recopier sur cette ligne tant que l’inventaire n’est pas saisi.
              </Notice>
            </div>
          )}

          {(ecarte.attenteN > 0 || ecarte.nonStatues > 0 || attenteAnciennes.length > 0) && (
            <div style={{ marginTop: 8 }}>
              <Notice c="warn">
                <b>Ce que les totaux PAD ne contiennent pas.</b>{' '}
                {ecarte.attenteN > 0 && (
                  <>
                    {ecarte.attenteN} PAD en attente ({fmtEur(ecarte.attenteMontant)} annoncés par
                    ORIKA) : sans justificatif ni télécollecte, cet argent n’est pas entré
                    aujourd’hui — il repart demain et ne se saisit pas ici.{' '}
                  </>
                )}
                {ecarte.nonStatues > 0 && (
                  <>
                    {ecarte.nonStatues} PAD sans issue choisie à l’étape 03 : tant qu’on ne sait
                    pas s’ils sont entrés, les compter serait l’affirmer. Les totaux carte et
                    chèque ci-dessus sont donc incomplets.{' '}
                  </>
                )}
                {attenteAnciennes.length > 0 && (
                  <>
                    {pluriel(attenteAnciennes.length, 'dossier')} d’une journée précédente
                    {attenteAnciennes.length > 1 ? ' sont' : ' est'} encore ouvert
                    {attenteAnciennes.length > 1 ? 's' : ''} (
                    {fmtEur(+attenteAnciennes.reduce((s, p) => s + (Number(p.montant_attendu) || 0), 0).toFixed(2))}
                    ) : rien ne se saisit ici tant qu’ils ne sont pas soldés à l’étape 03.
                  </>
                )}
              </Notice>
            </div>
          )}
        </div>

        <div>
          <Card titre="Documents à imprimer"
                lampe={tousCoches ? 'ok' : 'warn'}
                actions={<Chip c={tousCoches ? 'ok' : 'neutre'}>{nbCoches}/{aImprimer.length}</Chip>}>
            {/* L'action la plus fréquente de la carte, donc la plus haute et la
                plus grande : imprimer rapport par rapport, c'est autant
                d'allers-retours entre l'écran et l'imprimante. */}
            <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--line)' }}>
              <button type="button" className="btn pri lg" style={{ width: '100%' }}
                      data-encours={toutEnCours ? 'true' : undefined}
                      disabled={toutEnCours || aImprimer.length === 0}
                      onClick={() => void toutImprimer()}>
                {toutEnCours ? 'Fusion en cours…'
                  : aImprimer.length === 1 ? 'Télécharger le document'
                  : `Télécharger les ${aImprimer.length} documents`}
              </button>
              <div className="hint" style={{ marginTop: 6 }}>
                Un seul PDF fusionné, avec une page blanche d’alignement quand un rapport se
                termine sur une page impaire — la liasse s’imprime en recto-verso sans décalage.
              </div>
            </div>

            {aImprimer.length === 0 && (
              <Vide>
                Aucun des {REPORTS_META.length} rapports n’a de données aujourd’hui —
                rien à imprimer.
              </Vide>
            )}

            {REPORTS_META.map((r) => {
              const vide = !r.hasData(appData)
              const coche = !!coches[r.id]
              const occupe = !!enCours[r.id]
              return (
                <div className={`docrow${coche ? ' checked' : ''}`} key={r.id}
                     style={vide ? { opacity: 0.5 } : undefined}>
                  <input id={`doc-${r.id}`} type="checkbox" checked={coche}
                         disabled={lectureSeule || vide}
                         onChange={() => enregistrer((c) => ({ ...c, [r.id]: !c[r.id] }))} />
                  <label htmlFor={`doc-${r.id}`}
                         style={{ cursor: lectureSeule || vide ? 'default' : 'pointer', minWidth: 0 }}>
                    <span>{r.label}</span>
                  </label>
                  <span className="sp" />
                  {vide ? (
                    <span className="hint" style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}>
                      SANS OBJET
                    </span>
                  ) : (
                    <button type="button" className="btn sm"
                            data-encours={occupe ? 'true' : undefined}
                            disabled={occupe || toutEnCours}
                            onClick={() => void imprimer(r.id)}>
                      {occupe ? 'Génération…' : coche ? 'Réimprimer' : 'Imprimer'}
                    </button>
                  )}
                </div>
              )
            })}

            {/* Cocher tout d'un geste est réversible : le geste inverse existe,
                sans confirmation, sinon un clic malheureux ferme l'étape à tort. */}
            {nbCoches > 0 && !lectureSeule && (
              <div style={{ padding: '8px 12px', borderTop: '1px solid var(--line)' }}>
                <button type="button" className="btn gh sm" onClick={() => enregistrer(() => ({}))}>
                  Décocher les {nbCoches} documents
                </button>
              </div>
            )}
          </Card>

          <div style={{ marginTop: 12 }}>
            <Chantier titre="Le rapport final ORIKA du serveur">
              Le bouton « Rapport final » de la V2 ouvre <span className="mo">/api/generate-pdf</span>,
              une route serveur dont le format ORIKA ne doit pas être modifié. Elle reste
              injoignable ici : FlashCP est un site statique sans serveur, et seuls les{' '}
              {REPORTS_META.length} rapports générés dans le navigateur sont disponibles.
            </Chantier>
          </div>
        </div>
      </div>
    </>
  )
}

/**
 * Copier un texte, y compris hors contexte sécurisé.
 *
 * L'entrée de service du poste est `http://100.75.56.118:5181` (README) : en
 * HTTP simple, `navigator.clipboard` n'existe pas. L'appel optionnel de
 * `useCopier` court-circuite alors la chaîne entière — pas d'exception, pas de
 * toast, pas de copie : le seul geste de cet écran échouait EN SILENCE sur le
 * poste réel. On retombe donc sur la sélection + execCommand, qui, elle, marche
 * en HTTP. Correctif à porter dans ui/Toast.tsx (fichier partagé, non touché).
 */
async function copierTexte(texte: string): Promise<boolean> {
  try {
    if (window.isSecureContext && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(texte)
      return true
    }
  } catch { /* le presse-papier a refusé : on tente la voie de secours */ }
  try {
    const zone = document.createElement('textarea')
    zone.value = texte
    zone.setAttribute('readonly', '')
    zone.style.position = 'fixed'
    zone.style.top = '0'
    zone.style.opacity = '0'
    document.body.appendChild(zone)
    zone.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(zone)
    return ok
  } catch { return false }
}

/**
 * Ce qui part dans le presse-papier n'est pas ce qui s'affiche : COL MNC attend
 * un nombre. `fmtEur` pose une espace insécable de milliers et le symbole € —
 * collés dans un champ montant ils sont refusés ou tronqués. On copie donc
 * « 1234,56 », virgule décimale française, signe conservé pour un écart.
 */
const pourCopie = (n: number): string =>
  (Math.round((Number(n) || 0) * 100) / 100).toFixed(2).replace('.', ',')

/** Deux jeux de coches disent-ils la même chose ? (clé absente = non cochée) */
function memesCoches(a: Record<string, boolean>, b: Record<string, boolean>): boolean {
  for (const cle of new Set([...Object.keys(a), ...Object.keys(b)])) {
    if (!!a[cle] !== !!b[cle]) return false
  }
  return true
}

/**
 * Une ligne à dicter — la même forme pour les quatre écrans de COL MNC et pour
 * les totaux de la remise. C'est `@/domain/colmnc` qui les produit ; l'écran ne
 * fait que les rendre copiables.
 */
interface Ligne {
  cle: string
  source?: string
  /** COMBIEN d'objets derrière le montant — « 31 bons », « 2 PAD ». */
  compte?: string
  valeur: number
  /** Un écart : couleur et signe obéissent à @/domain/ecart, jamais au signe seul. */
  ecart?: boolean
  /** La valeur est un COMPTE (pièces, billets), pas un montant en euros. */
  nombre?: boolean
  /** Du texte à recopier tel quel — le commentaire d'un écart, un motif. */
  texte?: string
  /** Une seconde valeur sur la même ligne — les rouleaux à côté des pièces. */
  paire?: { libelle: string; valeur: number; source?: string }
  /** Chiffre indéterminé — ni zéro, ni conforme : il n'y a rien à recopier. */
  indetermine?: boolean
  /** Correction déjà incluse dans la valeur (montant réel − montant ORIKA). */
  correction?: number
}

/**
 * La clé de trace du « déjà copié ». Deux lignes peuvent porter le même nom
 * dans deux sections différentes — un caissier a un écart de caisse ET un bon —
 * et cocher l'une cocherait l'autre. La source les sépare.
 */
const cleLigne = (m: Ligne): string => `${m.cle}\u0000${m.source ?? ''}`

/**
 * La seconde valeur d'une ligne appariée, vue comme une ligne à part entière :
 * elle a sa propre trace de copie. Sans cela, copier les pièces marquerait les
 * rouleaux comme copiés — et une case de COL MNC resterait vide.
 */
const ligneDeLaPaire = (m: Ligne): Ligne | null =>
  m.paire
    ? { cle: `${m.cle} — ${m.paire.libelle}`, source: m.paire.source, valeur: m.paire.valeur, nombre: true }
    : null

/**
 * Une ligne à recopier. Trois choses la gouvernent :
 *
 *   • le MONTANT est ce qu'on vient chercher : colonne fixe à droite, gros,
 *     aligné d'une ligne à l'autre — on le lit debout, on le recopie ailleurs ;
 *   • le MOTIF d'un écart n'est pas un détail. COL MNC l'attend à côté du
 *     montant, et le retaper de mémoire le déforme : il a son propre bandeau,
 *     son propre bouton, et son absence se voit en ambre ;
 *   • la densité. Onze lignes de coffre plus six d'écarts se lisent d'un écran
 *     ou ne se lisent pas.
 */
function Rangee({ m, deja, fraiche, copier, copierTexte, copierPaire, dejaPaire, fraichePaire }: {
  m: Ligne
  deja: boolean
  fraiche: boolean
  copier: () => void
  copierTexte?: () => void
  copierPaire?: () => void
  dejaPaire?: boolean
  fraichePaire?: boolean
}) {
  const valeur = m.indetermine ? '—'
    : m.nombre ? String(m.valeur)
    : m.ecart ? fmtEcart(m.valeur) : fmtEur(m.valeur)
  return (
    <>
      <div className="drow">
        <span className="k">
          {m.cle}
          <s>
            {m.compte && <b>{m.compte}</b>}
            {m.compte && m.source ? ' · ' : ''}
            {m.source}
            {m.correction !== undefined && m.correction !== 0 && (
              <> · CORRIGÉ DE <span style={{ color: couleurEcart(m.correction) }}>
                {fmtEcart(m.correction)}
              </span></>
            )}
          </s>
        </span>
        <span className="sp" />
        <button type="button" className="dval" disabled={m.indetermine === true}
                onClick={copier}
                title={m.indetermine ? 'Chiffre indéterminé — rien à copier'
                  : m.ecart ? `${motEcart(m.valeur)} — copier ${pourCopie(m.valeur)}`
                  : `Copier ${pourCopie(m.valeur)} pour COL MNC`}
                style={{
                  // Le ton neutre porte --ink3 : un montant à recopier se lit à
                  // l'encre pleine, un écart à la couleur de sa gravité. Un
                  // chiffre INDÉTERMINÉ passe avant tout : sa valeur nominale
                  // est 0, et `couleurEcart(0)` rend --ok — le « — » d'un coffre
                  // non compté s'affichait donc en VERT, c'est-à-dire
                  // « conforme ». Il est gris : on ne sait pas.
                  color: m.indetermine ? 'var(--ink4)'
                    : m.ecart ? couleurEcart(m.valeur) : 'var(--ink)',
                  fontSize: m.nombre ? 19 : 22,
                }}>
          {valeur}
        </button>
        <button type="button" className="btn sm" disabled={m.indetermine === true}
                style={{ minWidth: 84, ...(fraiche ? FLASH_COPIE : null) }}
                onClick={copier}>
          {fraiche ? 'COPIÉ' : deja ? '✓' : 'Copier'}
        </button>

        {m.paire && (
          <>
            <span className="dsep">
              {m.paire.libelle}
              {m.paire.source && <s>{m.paire.source}</s>}
            </span>
            <button type="button" className="dval" onClick={copierPaire}
                    title={`Copier ${m.paire.valeur} — ${m.paire.libelle}`}
                    style={{ fontSize: 19, color: 'var(--ink)' }}>
              {m.paire.valeur}
            </button>
            <button type="button" className="btn sm"
                    style={{ minWidth: 84, ...(fraichePaire ? FLASH_COPIE : null) }}
                    onClick={copierPaire}>
              {fraichePaire ? 'COPIÉ' : dejaPaire ? '✓' : 'Copier'}
            </button>
          </>
        )}
      </div>

      {m.texte !== undefined && (
        <div className={`dmotif${m.texte ? '' : ' vide'}`}>
          <span className="q">
            {m.texte
              ? `« ${m.texte} »`
              : 'Motif non saisi — il se commente au dépouillement, étape 05a'}
          </span>
          <span className="sp" />
          {m.texte && copierTexte && (
            <button type="button" className="btn sm" style={{ minWidth: 84 }} onClick={copierTexte}>
              Copier
            </button>
          )}
        </div>
      )}
    </>
  )
}

/** Ce que les totaux PAD ne contiennent PAS, et pourquoi (§7.3). */
interface Ecarte { attenteN: number; attenteMontant: number; nonStatues: number }

/** Les sommes de flottants se recopient : on les arrête au centime à la source. */
const cents = (n: number): number => +(Number(n) || 0).toFixed(2)

function calculer(rapport: PropsEtape['rapport']): { lignes: Ligne[]; ecarte: Ecarte } {
  const payments = Array.isArray(rapport?.payments)
    ? (rapport.payments as Record<string, unknown>[]) : []
  const nonPad = payments.filter((p) => !isPadPayment(p as { reglementId?: number | null; typeReglement?: string | null }))
  const somme = (ids: number[]) => cents(nonPad
    .filter((p) => ids.includes(Number(p.reglementId)))
    .reduce((s, p) => s + (Number(p.montant) || 0), 0))
  const compte = (ids: number[]) => nonPad.filter((p) => ids.includes(Number(p.reglementId))).length

  const pad = etatPad(rapport)
  type LPad = (typeof pad.lignes)[number]

  // §7.3, issue « absent_ko » : pas de justificatif ET pas de télécollecte. Le
  // dossier part en file d'attente et poursuit les jours suivants — cet argent
  // n'est PAS entré aujourd'hui. `montant_reel` restant null, l'écart vaut 0 et
  // la ligne repartirait au montant ORIKA : elle gonflerait la saisie COL MNC
  // d'une somme jamais encaissée. Le montant réel d'un PAD en attente est 0.
  const enAttente = (l: LPad) => l.verif?.recu === false && l.verif.telecollecte === false

  // Montant réel = ORIKA corrigé de l'écart constaté à l'étape 03 (§7.3).
  const reel = (l: LPad) => l.montant + l.ecart

  // Un PAD sans issue choisie n'affirme rien : ni « reçu », ni « pas reçu ». Le
  // compter au montant ORIKA affirmerait qu'il est entré. On l'écarte et on le
  // dit — l'étape 11 rend des chiffres, elle n'en invente pas.
  const retenus = pad.lignes.filter((l) => l.statue && !enAttente(l))
  const padCarte  = retenus.filter((l) => l.verif?.type === 'carte')
  const padCheque = retenus.filter((l) => l.verif?.type === 'cheque')
  const padAttente = pad.lignes.filter(enAttente)
  const ecarte: Ecarte = {
    attenteN: padAttente.length,
    attenteMontant: cents(padAttente.reduce((s, l) => s + l.montant, 0)),
    nonStatues: pad.lignes.filter((l) => !l.statue).length,
  }
  const correction = (ls: LPad[]) => cents(ls.reduce((s, l) => s + l.ecart, 0))

  const coffre = etatCoffre(rapport)
  const inv = (rapport?.inventaireCoffre ?? {}) as { prelevements_j1?: Array<{ amount?: number }> }
  const enveloppes = inv.prelevements_j1 ?? []

  const lignes: Ligne[] = [
    { cle: 'Chèques — total', source: `CHQ AUTO + MANUEL · ${compte(CHEQUES)} pièces`, valeur: somme(CHEQUES) },
    {
      cle: 'PAD — carte', source: `${padCarte.length} pièces · montant réel`,
      valeur: cents(padCarte.reduce((s, l) => s + reel(l), 0)),
      correction: correction(padCarte),
    },
    {
      cle: 'PAD — chèque', source: `${padCheque.length} pièces · montant réel`,
      valeur: cents(padCheque.reduce((s, l) => s + reel(l), 0)),
      correction: correction(padCheque),
    },
    { cle: 'Bons divers', source: `${compte(REMISE_GROUPS.BONS)} bons ventilés`, valeur: somme(REMISE_GROUPS.BONS) },
    { cle: 'TR / TRP', source: `${compte(REMISE_GROUPS.TRP)} titres`, valeur: somme(REMISE_GROUPS.TRP) },
    { cle: 'CAP', source: `${compte(REMISE_GROUPS.CAP)} titres`, valeur: somme(REMISE_GROUPS.CAP) },
    { cle: 'Espèces — entrée coffre', source: `${enveloppes.length} enveloppes J+1`, valeur: cents(sumEnveloppesJ1(enveloppes)) },
    {
      cle: 'Écart de coffre',
      // Sans inventaire, `etatCoffre` rend 0 par prudence : ce 0 dirait
      // « conforme » en vert alors que rien n'a été compté. Un coffre non
      // compté n'a pas d'écart nul, il n'a pas d'écart connu.
      source: !coffre.existe ? 'inventaire non commencé — étape 10'
        : coffre.motifEcart.trim() ? 'motif renseigné'
        : Math.abs(coffre.ecart) >= 0.01 ? 'motif à saisir — étape 10'
        : 'aucun écart',
      valeur: cents(coffre.ecart),
      ecart: true,
      indetermine: !coffre.existe,
    },
  ]
  return { lignes, ecarte }
}
